// FilterBuilder.ts
import {escapeSql, quoteIdentifier} from './SqlUtils'
import {BooleanAdvancedFilterModel, ColumnAdvancedFilterModel, DateFilterModel, IMultiFilterModel, JoinAdvancedFilterModel, NumberFilterModel, SetFilterModel, TextFilterModel} from 'ag-grid-community'
import type {FilterModel} from 'ag-grid-community/dist/types/src/interfaces/iFilter'
import type {AdvancedFilterModel} from 'ag-grid-community/dist/types/src/interfaces/advancedFilterModel'

export class FilterBuilder {

    public static createWhereClause(
        filterModel: FilterModel | AdvancedFilterModel | null,
        groupKeys: string[],
        rowGroupCols: any[]
    ): string {
        const conditions: string[] = []

        // 1. Handle Group Drill-Downs (Implicit Filtering from expanding groups)
        groupKeys?.forEach((key, index) => {
            const col = rowGroupCols[index]
            const fieldName = col.field || col.id
            conditions.push(`${quoteIdentifier(fieldName)} = ${escapeSql(key)}`)
        })

        if (!filterModel) {
            return conditions.length > 0 ? `where ${conditions.join(' and ')}` : ''
        }

        // 2. Handle Explicit User Filters
        // Check if it's an Advanced Filter (Tree Structure) or Standard Filter (Map)
        if (this.isAdvancedFilter(filterModel)) {
            const advancedCondition = this.processAdvancedFilter(filterModel)
            if (advancedCondition) {
                conditions.push(advancedCondition)
            }
        } else {
            // Standard Filter: Iterate over column keys
            const modelMap = filterModel as FilterModel
            Object.keys(modelMap).forEach(colId => {
                const filter = modelMap[colId]
                const condition = this.processStandardFilter(colId, filter)
                if (condition) {
                    conditions.push(condition)
                }
            })
        }

        return conditions.length > 0 ? `where ${conditions.join(' and ')}` : ''
    }

    /**
     * Discriminator to check if the model is an AdvancedFilterModel (has filterType at root)
     */
    private static isAdvancedFilter(model: any): model is AdvancedFilterModel {
        return model && typeof model === 'object' && 'filterType' in model
    }

    // --- Advanced Filter Logic ---

    private static processAdvancedFilter(model: AdvancedFilterModel): string | null {
        if (model.filterType === 'join') {
            const join = model as JoinAdvancedFilterModel
            const subConditions = join.conditions
                .map(c => this.processAdvancedFilter(c))
                .filter(Boolean)

            if (subConditions.length === 0) return null
            return `(${subConditions.join(` ${join.type} `)})`
        } else {
            // It's a column filter (Leaf node in the tree)
            const colFilter = model as ColumnAdvancedFilterModel
            // We can reuse the standard logic, but we need to extract colId
            return this.processStandardFilter(colFilter.colId, colFilter as any)
        }
    }

    // --- Standard Filter Logic ---

    private static processStandardFilter(colId: string, filter: FilterModel): string | null {
        const colName = quoteIdentifier(colId)

        // Handle Leaf Filters
        switch (filter.filterType) {
            case 'text':
                return this.getTextFilter(colName, filter as TextFilterModel)
            case 'number':
                return this.getNumberFilter(colName, filter as NumberFilterModel)
            case 'date':
                return this.getDateFilter(colName, filter as DateFilterModel)
            case 'set':
                return this.getSetFilter(colName, filter as SetFilterModel)
            case 'multi':
                return this.getMultiFilter(colId, filter as IMultiFilterModel)
            case 'boolean':
                return this.getBooleanFilter(colName, filter as unknown as BooleanAdvancedFilterModel)
            default:
                return null
        }
    }

    // --- Specific Filter Type Implementations ---

    private static getMultiFilter(colId: string, model: IMultiFilterModel): string | null {
        // AG Grid Multi Filter logic applies AND between all active child filters
        const conditions = model.filterModels
            ?.filter(m => m !== null) // Filter out inactive (null) filters
            ?.map(m => this.processStandardFilter(colId, m!)) // Recursively process child
            ?.filter(Boolean) ?? []

        if (conditions.length === 0) return null
        return `(${conditions.join(' and ')})`
    }

    private static getTextFilter(col: string, model: TextFilterModel): string {
        const val = model.filter ? model.filter.replace(/'/g, '\'\'') : ''

        switch (model.type) {
            case 'equals':
                return `${col} = '${val}'`
            case 'notEqual':
                return `${col} != '${val}'`
            case 'contains':
                return `${col} ilike '%${val}%'`
            case 'notContains':
                return `${col} not ilike '%${val}%'`
            case 'startsWith':
                return `${col} ilike '${val}%'`
            case 'endsWith':
                return `${col} ilike '%${val}'`
            case 'blank':
                return `(${col} is null or ${col} = '')`
            case 'notBlank':
                return `(${col} is not null and ${col} != '')`
            default:
                return '1=1'
        }
    }

    private static getNumberFilter(col: string, model: NumberFilterModel): string {
        const val = model.filter
        const valTo = model.filterTo

        switch (model.type) {
            case 'equals':
                return `${col} = ${val}`
            case 'notEqual':
                return `${col} != ${val}`
            case 'greaterThan':
                return `${col} > ${val}`
            case 'greaterThanOrEqual':
                return `${col} >= ${val}`
            case 'lessThan':
                return `${col} < ${val}`
            case 'lessThanOrEqual':
                return `${col} <= ${val}`
            case 'inRange':
                return `(${col} >= ${val} and ${col} <= ${valTo})`
            case 'blank':
                return `${col} is null`
            case 'notBlank':
                return `${col} is not null`
            default:
                return '1=1'
        }
    }

    private static getDateFilter(col: string, model: DateFilterModel): string {
        const val = model.dateFrom ? `'${model.dateFrom}'` : 'null'
        const valTo = model.dateTo ? `'${model.dateTo}'` : 'null'

        const colDate = `cast(${col} as date)`
        const v1 = `cast(${val} as date)`
        const v2 = `cast(${valTo} as date)`

        switch (model.type) {
            case 'equals':
                return `${colDate} = ${v1}`
            case 'notEqual':
                return `${colDate} != ${v1}`
            case 'greaterThan':
                return `${colDate} > ${v1}`
            case 'lessThan':
                return `${colDate} < ${v1}`
            case 'inRange':
                return `(${colDate} >= ${v1} and ${colDate} <= ${v2})`
            case 'blank':
                return `${col} is null`
            case 'notBlank':
                return `${col} is not null`
            default:
                return '1=1'
        }
    }

    private static getSetFilter(col: string, model: SetFilterModel): string {
        if (!model.values || model.values.length === 0) return '1=0'

        const hasNull = model.values.includes(null)
        const nonNullValues = model.values.filter(v => v !== null)

        const parts = []
        if (nonNullValues.length > 0) {
            const values = nonNullValues.map(v => escapeSql(v)).join(', ')
            parts.push(`${col} in (${values})`)
        }
        if (hasNull) {
            parts.push(`${col} is null`)
        }

        return parts.length > 1 ? `(${parts.join(' or ')})` : parts[0]
    }

    private static getBooleanFilter(col: string, model: BooleanAdvancedFilterModel): string {
        return `${col} = ${model.type}`
    }
}